"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useDropdownContext = exports.SET_BUTTON_ID = exports.SELECT_MENU_ITEM = exports.SEARCH_FOR_ITEM = exports.OPEN_MENU_CLEARED = exports.OPEN_MENU_AT_INDEX = exports.OPEN_MENU_AT_FIRST_ITEM = exports.DropdownProvider = exports.DropdownDescendantContext = exports.CLOSE_MENU = exports.CLEAR_ACTIVE_INDEX = exports.ACTIVE_ITEM_AT_INDEX = void 0;
exports.useDropdownDescendants = useDropdownDescendants;
exports.useItemId = useItemId;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _createContext = _interopRequireDefault(require("@ux/component-utilities/create-context"));
var _useCallbackRef = _interopRequireDefault(require("@ux/component-utilities/use-callback-ref"));
var _descendants = require("../descendants");
var _util = require("../util");
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; } /* eslint-disable complexity */
const CLEAR_ACTIVE_INDEX = exports.CLEAR_ACTIVE_INDEX = 'CLEAR_ACTIVE_INDEX';
const SELECT_MENU_ITEM = exports.SELECT_MENU_ITEM = 'SELECT_MENU_ITEM';
const CLOSE_MENU = exports.CLOSE_MENU = 'CLOSE_MENU';
const OPEN_MENU_AT_FIRST_ITEM = exports.OPEN_MENU_AT_FIRST_ITEM = 'OPEN_MENU_AT_FIRST_ITEM';
const OPEN_MENU_AT_INDEX = exports.OPEN_MENU_AT_INDEX = 'OPEN_MENU_AT_INDEX';
const OPEN_MENU_CLEARED = exports.OPEN_MENU_CLEARED = 'OPEN_MENU_CLEARED';
const SEARCH_FOR_ITEM = exports.SEARCH_FOR_ITEM = 'SEARCH_FOR_ITEM';
const ACTIVE_ITEM_AT_INDEX = exports.ACTIVE_ITEM_AT_INDEX = 'ACTIVE_ITEM_AT_INDEX';
const SET_BUTTON_ID = exports.SET_BUTTON_ID = 'SET_BUTTON_ID';
const DropdownDescendantContext = exports.DropdownDescendantContext = (0, _descendants.createDescendantContext)('DropdownDescendantContext');
const [DropdownProvider, useDropdownContext] = (0, _createContext.default)('Dropdown');

/**
 * @type DropdownState
 * @property {string|null} triggerId HTMLElement id used for trigger element, MenuButton
 * @property {boolean} isExpanded Is the menu open or closed
 * @property {string} typeaheadQuery Tracks the typeahead when applying focus to items
 * @property {number} activeIndex Current visually focused item index
 * @property {number} selectedIndex Current selected item index
 */
exports.useDropdownContext = useDropdownContext;
const initialState = {
  triggerId: null,
  isExpanded: false,
  typeaheadQuery: '',
  activeIndex: -1,
  selectedIndex: -1
};

/**
 * Dropdown provider sets up the context/state of the dropdown
 * @param {Object} props DropdownProvder props
 * @param {string} props.id HTMLElement id property used for menu
 * @param {React.ReactNode} props.children The children should contain a trigger and a list
 * @param {boolean} [props.closeOnSelect=true] Should menu remained open after selection
 * @param {boolean} [props.rtl=false] Sets the navigation to right to left
 * @param {boolean} [props.isTouch=false] Is disrupt used instead of flyout for popover
 * @param {function} [props.onSelect] The onSelect can be applied at the menu level or at the item level
 * @param {string} [props.size] Spacing format to use for menu items
 * @returns {React.ChildContextProvider} Descendant and Dropdown providers
 */
const DropdownProvider_ = _ref => {
  let {
    id,
    children,
    closeOnSelect = true,
    rtl = false,
    isTouch = false,
    onSelect,
    size
  } = _ref;
  const triggerRef = _react.default.useRef(null);
  const dropdownRef = _react.default.useRef(null);
  const popoverRef = _react.default.useRef(null);
  const [descendants, setDescendants] = (0, _descendants.useDescendantsInit)();
  const dropdownId = id || (0, _util.generateId)(id, 'menu');
  const triggerId = (0, _util.generateId)(dropdownId, 'trigger');
  const listId = (0, _util.generateId)(dropdownId, 'list');
  const [state, dispatch] = _react.default.useReducer(dropDownReducer, _objectSpread(_objectSpread({}, initialState), {}, {
    triggerId
  }));

  // We will put children callbacks in a ref to avoid triggering endless render
  // loops when using render props if the app code doesn't useCallback
  // https://github.com/reach/reach-ui/issues/523
  const selectCallbacks = _react.default.useRef([]);

  // If the popover's position overlaps with an option when the popover
  // initially opens, the mouseup event will trigger a select. To prevent that,
  // we decide the control is only ready to make a selection if the pointer
  // moves a certain distance OR if the mouse button is pressed for a certain
  // length of time, otherwise the user is just registering the initial button
  // click rather than selecting an item.
  // For context on some implementation details, see https://github.com/reach/reach-ui/issues/563
  const readyToSelect = _react.default.useRef(false);
  const mouseDownStartPosRef = _react.default.useRef({
    x: 0,
    y: 0
  });
  _react.default.useEffect(() => {
    if (state.isExpanded) {
      window.requestAnimationFrame(() => {
        var _dropdownRef$current;
        (_dropdownRef$current = dropdownRef.current) === null || _dropdownRef$current === void 0 ? void 0 : _dropdownRef$current.focus();
      });
    }
  }, [state.isExpanded]);
  return /*#__PURE__*/_react.default.createElement(_descendants.DescendantProvider, {
    context: DropdownDescendantContext,
    items: descendants,
    set: setDescendants
  }, /*#__PURE__*/_react.default.createElement(DropdownProvider, {
    closeOnSelect: closeOnSelect,
    dispatch: dispatch,
    dropdownId: dropdownId,
    dropdownRef: dropdownRef,
    isTouch: isTouch,
    listId: listId,
    mouseDownStartPosRef: mouseDownStartPosRef,
    onSelect: (0, _useCallbackRef.default)(onSelect),
    popoverRef: popoverRef,
    readyToSelect: readyToSelect,
    rtl: rtl,
    selectCallbacks: selectCallbacks,
    size: size,
    state: state,
    triggerRef: triggerRef
  }, children));
};
exports.DropdownProvider = DropdownProvider_;
DropdownProvider_.propTypes = {
  children: _propTypes.default.node.isRequired,
  closeOnSelect: _propTypes.default.bool,
  id: _propTypes.default.string.isRequired,
  isTouch: _propTypes.default.bool,
  onSelect: _propTypes.default.func,
  rtl: _propTypes.default.bool,
  size: _propTypes.default.oneOf(['small'])
};
DropdownProvider_.displayName = 'DropdownProvider';

/**
 * @type DropdownAction
 * @property {string} type
 * @property {DropdownState} payload
 */

/**
 * Dropdown Reducer for managing state
 * @param {DropdownState} state Dropdown state
 * @param {DropdownAction} action Action being dispatched
 * @returns {DropdownState} next state
 */
function dropDownReducer(state) {
  let action = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  switch (action.type) {
    case SELECT_MENU_ITEM:
      return _objectSpread(_objectSpread({}, state), {}, {
        selectedIndex: action.payload.index
      });
    case CLOSE_MENU:
      {
        var _triggerRef$current;
        const {
          triggerRef = {
            current: null
          }
        } = action.payload;
        (_triggerRef$current = triggerRef.current) === null || _triggerRef$current === void 0 ? void 0 : _triggerRef$current.focus();
        return _objectSpread(_objectSpread({}, state), {}, {
          isExpanded: false,
          activeIndex: -1,
          selectedIndex: -1
        });
      }
    case OPEN_MENU_AT_FIRST_ITEM:
      return _objectSpread(_objectSpread({}, state), {}, {
        isExpanded: true,
        activeIndex: 0
      });
    case OPEN_MENU_AT_INDEX:
      return _objectSpread(_objectSpread({}, state), {}, {
        isExpanded: true,
        activeIndex: action.payload.index
      });
    case OPEN_MENU_CLEARED:
      return _objectSpread(_objectSpread({}, state), {}, {
        isExpanded: true,
        activeIndex: -1,
        selectedIndex: -1
      });
    case ACTIVE_ITEM_AT_INDEX:
      {
        const {
          dropdownRef = {
            current: null
          }
        } = action.payload;
        if (action.payload.index >= 0 && action.payload.index !== state.activeIndex) {
          if (dropdownRef.current) {
            const doc = (0, _util.getOwnerDocument)(dropdownRef.current);
            if (dropdownRef.current !== (doc === null || doc === void 0 ? void 0 : doc.activeElement)) {
              dropdownRef.current.focus();
            }
          }
          return _objectSpread(_objectSpread({}, state), {}, {
            activeIndex: action.payload.max != null ? Math.min(Math.max(action.payload.index, 0), action.payload.max) : Math.max(action.payload.index, 0)
          });
        }
        return state;
      }
    case CLEAR_ACTIVE_INDEX:
      return _objectSpread(_objectSpread({}, state), {}, {
        activeIndex: -1
      });
    case SET_BUTTON_ID:
      return _objectSpread(_objectSpread({}, state), {}, {
        triggerId: action.payload
      });
    case SEARCH_FOR_ITEM:
      if (typeof action.payload !== 'undefined') {
        return _objectSpread(_objectSpread({}, state), {}, {
          typeaheadQuery: action.payload
        });
      }
      return state;
    default:
      return state;
  }
}

/**
 * Helper to access descendants from the descendants context
 * @returns {array} Descendants/items array
 */
function useDropdownDescendants() {
  return (0, _descendants.useDescendants)(DropdownDescendantContext);
}

/**
 * Helper to get/generate item id
 * @param {number} index item index used for generating HTML id
 * @returns {string|null} item HTML id
 */
function useItemId(index) {
  const {
    dropdownId
  } = useDropdownContext('useItemId');
  return index != null && index > -1 ? (0, _util.generateId)(dropdownId, `option-${index}`) : null;
}